import { useEffect, useRef } from 'react';
import { cacheManager } from '@/utils/cache-manager';

interface SubscriptionError {
  channel: string;
  status: string;
  timestamp: number;
  retryCount: number;
}

class SubscriptionErrorTracker {
  private static instance: SubscriptionErrorTracker;
  private errors: Map<string, SubscriptionError> = new Map();
  private maxErrorsPerChannel = 5; // Aumentado para ser menos agressivo
  private errorWindowMs = 10 * 60 * 1000; // 10 minutos - janela maior para erros
  private globalErrorThreshold = 15; // Aumentado significativamente para ser menos agressivo
  private lastGlobalRefresh = 0;
  private globalRefreshCooldown = 20 * 60 * 1000; // 20 minutos entre refreshes globais
  private isRefreshing = false;

  static getInstance(): SubscriptionErrorTracker {
    if (!SubscriptionErrorTracker.instance) {
      SubscriptionErrorTracker.instance = new SubscriptionErrorTracker();
    }
    return SubscriptionErrorTracker.instance;
  }

  // Registrar erro de subscrição
  registerError(channel: string, status: string): void {
    const now = Date.now();
    const existingError = this.errors.get(channel);

    if (existingError) {
      // Se o erro é recente, incrementar contador
      if (now - existingError.timestamp < this.errorWindowMs) {
        existingError.retryCount++;
        existingError.timestamp = now;
      } else {
        // Reset se passou muito tempo
        existingError.retryCount = 1;
        existingError.timestamp = now;
      }
    } else {
      // Novo erro
      this.errors.set(channel, {
        channel,
        status,
        timestamp: now,
        retryCount: 1
      });
    }

    // Verificar se deve forçar refresh global (com cooldown)
    this.checkGlobalHealth();
  }

  // Verificar saúde global
  private checkGlobalHealth(): void {
    // Evitar múltiplos refreshes simultâneos
    if (this.isRefreshing) {
      return;
    }

    const now = Date.now();

    // Verificar cooldown para refresh global
    if (now - this.lastGlobalRefresh < this.globalRefreshCooldown) {
      return;
    }

    let totalErrors = 0;
    let criticalChannels = 0;
    let recentErrors = 0;

    // Limpar erros antigos e contar erros recentes
    for (const [channel, error] of this.errors.entries()) {
      if (now - error.timestamp > this.errorWindowMs) {
        this.errors.delete(channel);
      } else {
        totalErrors += error.retryCount;
        if (error.retryCount >= this.maxErrorsPerChannel) {
          criticalChannels++;
        }
        // Contar erros dos últimos 2 minutos
        if (now - error.timestamp < 2 * 60 * 1000) {
          recentErrors += error.retryCount;
        }
      }
    }

    // Lógica mais inteligente para refresh global
    const shouldRefresh = (
      // Muitos erros totais
      totalErrors >= this.globalErrorThreshold ||
      // Muitos canais críticos
      criticalChannels >= 3 ||
      // Muitos erros recentes (indicando problema ativo)
      recentErrors >= 10
    );

    if (shouldRefresh &&
      !this.isRefreshing &&
      (now - this.lastGlobalRefresh >= this.globalRefreshCooldown)) {
      console.warn(`Problemas de subscrição detectados: ${totalErrors} erros totais, ${criticalChannels} canais críticos, ${recentErrors} erros recentes`);
      console.warn('Iniciando refresh global...');
      this.forceGlobalRefresh();
    }
  }

  // Forçar refresh global
  private async forceGlobalRefresh(): Promise<void> {
    if (this.isRefreshing) {
      return;
    }

    try {
      this.isRefreshing = true;
      this.lastGlobalRefresh = Date.now();

      console.log('Iniciando refresh global automático...');

      // Primeiro, tentar limpar apenas o cache problemático
      try {
        // Limpar cache específico das tabelas com problemas
        for (const [channel] of this.errors.entries()) {
          if (channel.includes(':')) {
            const tableName = channel.split(':')[1];
            const cacheKey = `supabase_public_${tableName}`;
            cacheManager.removeCache(cacheKey);
            console.log(`Cache limpo para tabela problemática: ${tableName}`);
          }
        }
      } catch (cacheError) {
        console.warn('Erro ao limpar cache específico:', cacheError);
      }

      // Limpar rastreador de erros
      this.errors.clear();

      // Aguardar mais tempo antes de recarregar a página
      setTimeout(() => {
        console.log('Recarregando página após limpeza de cache...');
        window.location.reload();
      }, 10000); // Aumentado para 10 segundos
    } catch (error) {
      console.error('Erro ao forçar refresh global:', error);
      this.isRefreshing = false;
    }
  }

  // Reset contador de erros para um canal
  resetChannelErrors(channel: string): void {
    this.errors.delete(channel);
  }

  // Obter estatísticas de erros
  getErrorStats(): { totalErrors: number; criticalChannels: number } {
    const now = Date.now();
    let totalErrors = 0;
    let criticalChannels = 0;

    for (const error of this.errors.values()) {
      if (now - error.timestamp <= this.errorWindowMs) {
        totalErrors += error.retryCount;
        if (error.retryCount >= this.maxErrorsPerChannel) {
          criticalChannels++;
        }
      }
    }

    return { totalErrors, criticalChannels };
  }

  // Verificar se está em processo de refresh
  isCurrentlyRefreshing(): boolean {
    return this.isRefreshing;
  }
}

export const errorTracker = SubscriptionErrorTracker.getInstance();

// Hook para monitorar automaticamente a saúde das subscrições
export function useSubscriptionAutoRefresh() {
  const isInitialized = useRef(false);

  useEffect(() => {
    if (isInitialized.current) return;
    isInitialized.current = true;

    // Verificar saúde das subscrições periodicamente (menos frequente)
    const healthCheckInterval = setInterval(() => {
      const stats = errorTracker.getErrorStats();

      if (stats.totalErrors > 0) {
        console.log(`Status das subscrições: ${stats.totalErrors} erros, ${stats.criticalChannels} canais críticos`);
      }
    }, 120000); // A cada 2 minutos (reduzido de 60s para 120s)

    // Verificar conectividade periodicamente (menos frequente)
    const connectivityCheckInterval = setInterval(async () => {
      try {
        const isHealthy = await cacheManager.checkSupabaseConnection();
        if (!isHealthy) {
          console.warn('Problemas de conectividade detectados');
          // Não forçar refresh imediatamente, apenas registrar
        }
      } catch (error) {
        console.warn('Erro ao verificar conectividade:', error);
      }
    }, 300000); // A cada 5 minutos (reduzido de 120s para 300s)

    return () => {
      clearInterval(healthCheckInterval);
      clearInterval(connectivityCheckInterval);
    };
  }, []);

  // Função para registrar erro manualmente (usada pelo hook de subscrição)
  const registerSubscriptionError = (channel: string, status: string) => {
    errorTracker.registerError(channel, status);
  };

  // Função para resetar erros de um canal
  const resetChannelErrors = (channel: string) => {
    errorTracker.resetChannelErrors(channel);
  };

  return {
    registerSubscriptionError,
    resetChannelErrors,
    getErrorStats: () => errorTracker.getErrorStats(),
    isRefreshing: () => errorTracker.isCurrentlyRefreshing()
  };
}
